import {
  calcCompensationForShift,
  calcCompensationForNormalDeliveries,
  avgDeviationCompPercentage,
} from '../../../utils/costConstants';
import { CourierDeliveryZoneCountsByDate } from '../../../types/reportTypes';
import {
  ShiftCourierJson,
  ExtraWork,
  ExtraWorkConst,
} from '../../../const/ShiftConst';
import { divideBySafely } from './divideBySafely';
import { ExtraWorkCompensation, PerDayInvoice } from './arrayManipulation2';
import { CalcExtraByTypeSortCompensation } from './CalcExtraByTypeSortCompensation';

export function calcCostPerDayInvoice(
  courierDeliveryZoneCountsByDate: CourierDeliveryZoneCountsByDate,
  travelMode: string,
  shiftByDate: ShiftCourierJson,
  courierAvgRatingForDateRange: number,
  useOutsideOsloCompensationModel: boolean
): PerDayInvoice {
  const nrOfOrders = courierDeliveryZoneCountsByDate.totalCount;

  const compensationForShift = calcCompensationForShift(nrOfOrders, travelMode);

  const compensationForNormalDeliveries = calcCompensationForNormalDeliveries(
    nrOfOrders,
    travelMode,
    courierAvgRatingForDateRange,
    useOutsideOsloCompensationModel
  );

  const extraWorkCompensation: ExtraWorkCompensation =
    CalcExtraWorkCompensation(shiftByDate);
  const deviationCompensation: number = useOutsideOsloCompensationModel
    ? 0
    : getDeviationCompensation(compensationForNormalDeliveries);

  const totalCompensation: number = getTotalCompensation(
    compensationForShift,
    compensationForNormalDeliveries,
    extraWorkCompensation,
    deviationCompensation
  );
  return {
    date: courierDeliveryZoneCountsByDate.date,
    compensationForShift: compensationForShift,
    nrOfOrders: nrOfOrders,
    compensationForNormalDeliveries: compensationForNormalDeliveries,
    totalCompensation,
    productionCostPerDelivery: divideBySafely(totalCompensation, nrOfOrders),
    shift: shiftByDate,
    extraWorkCompensation,
    deviationCompensation,
  };
}

export const EmptyExtraWorkCompensation: ExtraWorkCompensation = {
  allExtraWorkCompensation: 0,
  sortWorkCompensation: 0,
  inboundWorkCompensation: 0,
  backupWorkCompensation: 0,
  otherWorkCompensation: 0,
};

export function CalcExtraWorkCompensation(
  shiftByDate: ShiftCourierJson
): ExtraWorkCompensation {
  const extraWorkList: ExtraWork[] = shiftByDate ? shiftByDate.extraWork : [];

  if (extraWorkList.length) {
    const sortWorkCompensation = CalcExtraByTypeSortCompensation(
      extraWorkList,
      ExtraWorkConst.SORT.typeOfExtraWork
    );

    const inboundWorkCompensation = CalcExtraByTypeSortCompensation(
      extraWorkList,
      ExtraWorkConst.INBOUND.typeOfExtraWork
    );

    const backupWorkCompensation = CalcExtraByTypeSortCompensation(
      extraWorkList,
      ExtraWorkConst.BACKUP.typeOfExtraWork
    );

    const allExtraWorkCompensation: number = extraWorkList
      .map(obj => obj.payment)
      .reduce((acc, cur) => acc + cur);

    const otherWorkCompensation =
      allExtraWorkCompensation -
      sortWorkCompensation -
      inboundWorkCompensation -
      backupWorkCompensation;

    return {
      allExtraWorkCompensation,
      sortWorkCompensation,
      inboundWorkCompensation,
      backupWorkCompensation,
      otherWorkCompensation,
    };
  } else {
    return EmptyExtraWorkCompensation;
  }
}

function getDeviationCompensation(
  compensationForNormalDeliveries: number
): number {
  return compensationForNormalDeliveries * avgDeviationCompPercentage;
}

function getTotalCompensation(
  compensationForShift: number,
  compensationForNormalDeliveries: number,
  extraWorkCompensation: ExtraWorkCompensation,
  deviationCompensation: number
): number {
  return (
    compensationForShift +
    compensationForNormalDeliveries +
    extraWorkCompensation.allExtraWorkCompensation +
    deviationCompensation
  );
}
